import React, { useState } from "react";
import { Box, Button, CircularProgress, Link, Typography } from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import StartPresentationDraftDialog from "./StartPresentationDraftDialog";
import { PresentationDraftStatus } from "../../../models/PresentationDraft";
import { LanguageCode } from "../../../models/enums/LanguageCode";
import { usePresentationDrafts, useRetryPresentationDraft } from "../../../hooks/usePresentationDrafts";
import { getLatestPublishedDraft, getOpenDraft, presentationReviewPath } from "../../../utils/presentationDraft";
import { formatDate } from "../../../utils/formatting";
import { zoullooColors } from "../../../theme";

interface PresentationDraftEntryProps {
	readonly freelancerProfileId: number;
	readonly freelancerProfileKey: string;
	readonly talentFirstName: string;
	readonly talentLanguage: LanguageCode;
}

const PresentationDraftEntry: React.FC<PresentationDraftEntryProps> = ({
	freelancerProfileId,
	freelancerProfileKey,
	talentFirstName,
	talentLanguage
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data: drafts, isLoading } = usePresentationDrafts(freelancerProfileId);
	const retryDraft = useRetryPresentationDraft();
	const [isStartOpen, setIsStartOpen] = useState(false);

	const openDraft = getOpenDraft(drafts);
	const publishedDraft = getLatestPublishedDraft(drafts);
	const openReview = () => navigate(presentationReviewPath(freelancerProfileKey));

	if (isLoading) {
		return null;
	}

	const renderOpenDraft = () => {
		if (!openDraft) return null;

		if (openDraft.status === PresentationDraftStatus.Generating) {
			return (
				<>
					<CircularProgress size={18} color="secondary" />
					<Box sx={{ flex: 1 }}>
						<Typography variant="body2" fontWeight={600}>
							{t("presentationDraft.entry.generating")}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{t("presentationDraft.entry.generatingHint")}
						</Typography>
					</Box>
				</>
			);
		}

		if (openDraft.status === PresentationDraftStatus.Failed) {
			return (
				<>
					<ErrorOutlineIcon color="error" fontSize="small" />
					<Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
						{t("presentationDraft.entry.failed")}
					</Typography>
					<Button size="small" onClick={openReview}>
						{t("presentationDraft.entry.open")}
					</Button>
					<Button
						size="small"
						variant="contained"
						color="secondary"
						disabled={retryDraft.isPending}
						onClick={() => retryDraft.mutate(openDraft.id)}
					>
						{t("presentationDraft.entry.retry")}
					</Button>
				</>
			);
		}

		return (
			<>
				<AutoAwesomeOutlinedIcon color="secondary" fontSize="small" />
				<Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
					{t("presentationDraft.entry.ready")}
				</Typography>
				<Button size="small" variant="contained" color="secondary" onClick={openReview}>
					{t("presentationDraft.entry.review")}
				</Button>
			</>
		);
	};

	return (
		<Box sx={{ mt: 2 }}>
			{openDraft ? (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						p: 1.5,
						borderRadius: "8px",
						backgroundColor: zoullooColors.secondary.alpha,
						border: `1px solid ${zoullooColors.secondary.light}`
					}}
				>
					{renderOpenDraft()}
				</Box>
			) : (
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
					<Button
						variant="outlined"
						color="secondary"
						size="small"
						startIcon={<AutoAwesomeOutlinedIcon />}
						onClick={() => setIsStartOpen(true)}
					>
						{t("presentationDraft.entry.start")}
					</Button>
					{publishedDraft && (
						<Typography variant="caption" color="text.secondary">
							{t("presentationDraft.entry.publishedFrom", {
								version: publishedDraft.version,
								date: formatDate(publishedDraft.publishedAt ?? undefined)
							})}{" "}
							<Link component="button" type="button" variant="caption" onClick={openReview}>
								{t("presentationDraft.entry.history")}
							</Link>
						</Typography>
					)}
				</Box>
			)}

			{isStartOpen && (
				<StartPresentationDraftDialog
					open
					onClose={() => setIsStartOpen(false)}
					freelancerProfileId={freelancerProfileId}
					talentName={talentFirstName}
					talentLanguage={talentLanguage}
					onStarted={() => setIsStartOpen(false)}
				/>
			)}
		</Box>
	);
};

export default PresentationDraftEntry;
